import React, { useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuChevronDown, LuX } from "react-icons/lu";
import {
  MAX_DELEGATIONS, MAX_TEXT, TRANSACTIONS, allowsResidential, attributesFor, categoriesFor, cities,
  coherentCriteria, delegationsOf, isLandOnly,
} from "../../utils/propertyRequestForm";
import { categoryLabel, cityLabel, delegationLabel } from "./labels";
import VoiceInputButton, { appendDictation } from "../VoiceInputButton";

export const fieldClass = "mt-1 block w-full min-w-0 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-900 shadow-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-500/20 aria-[invalid=true]:border-red-500";
const pill = (selected) => `inline-flex min-h-[44px] cursor-pointer items-center rounded-full border px-4 py-2 text-sm font-semibold transition focus-within:ring-2 focus-within:ring-green-500/30 ${selected ? "border-green-700 bg-green-700 text-white" : "border-slate-300 bg-white text-slate-800 hover:border-green-600"}`;

export function FieldError({ id, error }) {
  const { t } = useTranslation();
  if (!error) return null;
  return <p id={id} className="mt-1 text-sm font-medium text-red-700">{t(`propertyRequest.errors.${error}`)}</p>;
}

// Multi-select of up to 5 delegations: native select (usable on mobile) + removable chips.
export function DelegationPicker({ city, value, onChange, error }) {
  const { t } = useTranslation();
  const id = useId();
  const options = useMemo(() => delegationsOf(city)
    .filter((d) => !value.includes(d))
    .map((d) => ({ value: d, label: delegationLabel(t, city, d) }))
    .sort((a, b) => a.label.localeCompare(b.label)), [city, value, t]);
  const full = value.length >= MAX_DELEGATIONS;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-slate-800">
        {t("propertyRequest.criteria.delegations")} <span className="font-normal text-slate-500">({value.length}/{MAX_DELEGATIONS})</span>
      </label>
      <p className="text-sm text-slate-500">{city ? t("propertyRequest.criteria.delegationsHint", { max: MAX_DELEGATIONS }) : t("propertyRequest.criteria.chooseCityFirst")}</p>
      {value.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2" aria-label={t("propertyRequest.criteria.delegations")}>
          {value.map((d) => (
            <li key={d} className="inline-flex max-w-full items-center gap-1 rounded-full bg-green-50 py-1 ps-3 pe-1 text-sm font-semibold text-green-800">
              <span className="truncate">{delegationLabel(t, city, d)}</span>
              <button type="button" onClick={() => onChange(value.filter((x) => x !== d))}
                aria-label={t("propertyRequest.criteria.removeDelegation", { name: delegationLabel(t, city, d) })}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500/30">
                <LuX size={15} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <select id={id} className={fieldClass} value="" disabled={!city || full} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined}
        onChange={(e) => { if (e.target.value) onChange([...value, e.target.value]); }}>
        <option value="">{t("propertyRequest.criteria.addDelegation")}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <FieldError id={`${id}-error`} error={error} />
    </div>
  );
}

function NumberInput({ name, label, value, onChange, error }) {
  const id = useId();
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-sm font-semibold text-slate-800">{label}</label>
      <input id={id} name={name} type="text" inputMode="decimal" dir="ltr" autoComplete="off" className={fieldClass}
        value={value} maxLength={20} onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} />
      <FieldError id={`${id}-error`} error={error} />
    </div>
  );
}

// Free text + structured criteria. Values are canonical; only labels are translated.
// textFooter: actions under the text (analysis); beforeCriteria: content between the text and the criteria.
export default function PropertyRequestFields({ text, onTextChange, criteria, onCriteriaChange, errors = {}, textFooter = null, beforeCriteria = null }) {
  const { t } = useTranslation();
  const ids = { text: useId(), city: useId(), bedrooms: useId(), more: useId() };
  // Dictation results arrive asynchronously: they are appended to the latest text, not to a stale render.
  const latestText = useRef(text);
  latestText.current = text;
  const [moreOpen, setMoreOpen] = useState(Boolean(criteria.bedroomsMin || criteria.attributes.length));
  const update = (patch) => onCriteriaChange(coherentCriteria({ ...criteria, ...patch }));
  const toggle = (list, item) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
  const cityOptions = useMemo(() => cities().map((c) => ({ value: c, label: cityLabel(t, c) }))
    .sort((a, b) => a.label.localeCompare(b.label)), [t]);
  const unit = t(`propertyRequest.budgetUnit.${criteria.transaction || "sale"}`);

  return (
    <div className="space-y-5">
      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <label htmlFor={ids.text} className="block text-lg font-bold text-slate-900">{t("propertyRequest.text.label")}</label>
        <textarea id={ids.text} rows={4} maxLength={MAX_TEXT} value={text} onChange={(e) => onTextChange(e.target.value)}
          placeholder={t("propertyRequest.text.placeholder")} className={`${fieldClass} resize-y`}
          aria-invalid={Boolean(errors.text)} aria-describedby={`${ids.text}-notice`} />
        <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-500">
          <VoiceInputButton onText={(spoken) => onTextChange(appendDictation(latestText.current, spoken, MAX_TEXT))} />
          <bdi dir="ltr" className="ms-auto">{t("propertyRequest.text.count", { count: text.length, max: MAX_TEXT })}</bdi>
        </div>
        <FieldError id={`${ids.text}-error`} error={errors.text} />
        <p id={`${ids.text}-notice`} className="mt-2 text-sm text-slate-600">{t("propertyRequest.text.notice")} {t("propertyRequest.text.noContact")}</p>
        {textFooter}
      </section>

      {beforeCriteria}

      <section className="space-y-5 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <h2 className="text-lg font-bold text-slate-900">{t("propertyRequest.criteria.title")}</h2>

        <fieldset aria-describedby={errors.transaction ? "pr-transaction-error" : undefined}>
          <legend className="text-sm font-semibold text-slate-800">{t("propertyRequest.criteria.transaction")} <span className="font-normal text-slate-500">({t("propertyRequest.criteria.required")})</span></legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {TRANSACTIONS.map((tx) => (
              <label key={tx} className={pill(criteria.transaction === tx)}>
                <input type="radio" name="pr-transaction" value={tx} className="sr-only" checked={criteria.transaction === tx} onChange={() => update({ transaction: tx })} />
                {t(`propertyRequest.transaction.${tx}`)}
              </label>
            ))}
          </div>
          <FieldError id="pr-transaction-error" error={errors.transaction} />
        </fieldset>

        <fieldset aria-describedby={errors.categories ? "pr-categories-error" : undefined}>
          <legend className="text-sm font-semibold text-slate-800">{t("propertyRequest.criteria.categories")} <span className="font-normal text-slate-500">({t("propertyRequest.criteria.required")})</span></legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {categoriesFor(criteria.transaction).map((c) => (
              <label key={c} className={pill(criteria.categories.includes(c))}>
                <input type="checkbox" value={c} className="sr-only" checked={criteria.categories.includes(c)} onChange={() => update({ categories: toggle(criteria.categories, c) })} />
                {categoryLabel(t, c, criteria.transaction)}
              </label>
            ))}
          </div>
          <FieldError id="pr-categories-error" error={errors.categories} />
        </fieldset>

        <div>
          <label htmlFor={ids.city} className="block text-sm font-semibold text-slate-800">{t("propertyRequest.criteria.city")} <span className="font-normal text-slate-500">({t("propertyRequest.criteria.required")})</span></label>
          <select id={ids.city} className={fieldClass} value={criteria.city} onChange={(e) => update({ city: e.target.value })}
            aria-invalid={Boolean(errors.city)} aria-describedby={errors.city ? `${ids.city}-error` : undefined}>
            <option value="">{t("propertyRequest.criteria.chooseCity")}</option>
            {cityOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <FieldError id={`${ids.city}-error`} error={errors.city} />
        </div>

        <DelegationPicker city={criteria.city} value={criteria.delegations} error={errors.delegations} onChange={(delegations) => update({ delegations })} />

        <div className="grid grid-cols-2 gap-3">
          <NumberInput name="budgetMin" label={t("propertyRequest.criteria.budgetMin", { unit })} value={criteria.budgetMin} error={errors.budgetMin} onChange={(v) => update({ budgetMin: v })} />
          <NumberInput name="budgetMax" label={t("propertyRequest.criteria.budgetMax", { unit })} value={criteria.budgetMax} error={errors.budgetMax} onChange={(v) => update({ budgetMax: v })} />
          <NumberInput name="surfaceMin" label={t("propertyRequest.criteria.surfaceMin")} value={criteria.surfaceMin} error={errors.surfaceMin} onChange={(v) => update({ surfaceMin: v })} />
          <NumberInput name="surfaceMax" label={t("propertyRequest.criteria.surfaceMax")} value={criteria.surfaceMax} error={errors.surfaceMax} onChange={(v) => update({ surfaceMax: v })} />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50">
          <button type="button" aria-expanded={moreOpen} aria-controls={ids.more} onClick={() => setMoreOpen(!moreOpen)}
            className="flex min-h-[48px] w-full items-center justify-between gap-3 px-4 py-3 text-start text-base font-semibold text-slate-900">
            {t("propertyRequest.criteria.more")}
            <LuChevronDown aria-hidden="true" className={`shrink-0 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
          </button>
          {moreOpen && (
            <div id={ids.more} className="space-y-4 border-t border-slate-200 px-4 py-4">
              {!allowsResidential(criteria) ? (
                <p className="text-sm text-slate-600">{t(isLandOnly(criteria) ? "propertyRequest.criteria.landNote" : "propertyRequest.criteria.chooseTypeFirst")}</p>
              ) : (
                <>
                  <div>
                    <label htmlFor={ids.bedrooms} className="block text-sm font-semibold text-slate-800">{t("propertyRequest.criteria.bedroomsMin")}</label>
                    <select id={ids.bedrooms} className={fieldClass} value={criteria.bedroomsMin} onChange={(e) => update({ bedroomsMin: e.target.value })}
                      aria-invalid={Boolean(errors.bedroomsMin)}>
                      <option value="">{t("propertyRequest.summary.none")}</option>
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <option key={n} value={String(n)}>{n}</option>)}
                    </select>
                    <FieldError id={`${ids.bedrooms}-error`} error={errors.bedroomsMin} />
                  </div>
                  <fieldset>
                    <legend className="text-sm font-semibold text-slate-800">{t("propertyRequest.criteria.attributes")}</legend>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {attributesFor(criteria.transaction).map((a) => (
                        <label key={a} className={pill(criteria.attributes.includes(a))}>
                          <input type="checkbox" value={a} className="sr-only" checked={criteria.attributes.includes(a)} onChange={() => update({ attributes: toggle(criteria.attributes, a) })} />
                          {t(`propertyRequest.attribute.${a}`)}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
